import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Microphone, Stop, X, CheckCircle, Trash, Waveform, Sparkle } from "@phosphor-icons/react";

const APPLIANCE_TYPES = [
  "Refrigerator", "Range / Stove", "Oven", "Cooktop", "Microwave", "Dishwasher",
  "Washer", "Dryer", "Water Heater", "Furnace", "AC Condenser", "HVAC Air Handler",
  "Garbage Disposal", "Range Hood", "Ceiling Fan", "Smoke Detector", "Other",
];
const LOCATION_PRESETS = [
  "Kitchen", "Laundry Room", "Bathroom", "Basement", "Garage",
  "Utility", "Attic", "Hallway", "Living Room", "Bedroom",
];

const MAX_SECONDS = 180; // 3 minutes of narration is plenty for a full unit
const SILENT_STOP_MS = 4500; // auto-stop after 4.5 s of silence

// Phase state machine:
//   idle → recording → transcribing → review → saving → done
export default function VoiceWalk({ jobId, onClose, onSaved }) {
  const [phase, setPhase] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [rows, setRows] = useState([]);

  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceStartRef = useRef(null);
  const rafRef = useRef(null);

  const cleanupStream = () => {
    try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    recRef.current = null;
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    silenceStartRef.current = null;
  };
  useEffect(() => () => cleanupStream(), []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => sendAudio();
      rec.start();
      // Level meter + silence auto-stop
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        let sum = 0;
        for (const v of buf) sum += v;
        const avg = sum / buf.length; // 0..255
        setLevel(Math.min(1, avg / 90));
        // Silence auto-stop after 4.5 s of quiet
        if (avg < 6) {
          if (!silenceStartRef.current) silenceStartRef.current = Date.now();
          else if (Date.now() - silenceStartRef.current > SILENT_STOP_MS) { stop(); return; }
        } else {
          silenceStartRef.current = null;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      // Wall-clock cap
      timerRef.current = setInterval(() => {
        setSeconds(s => {
          const n = s + 1;
          if (n >= MAX_SECONDS) stop();
          return n;
        });
      }, 1000);
      setSeconds(0);
      setPhase("recording");
    } catch (e) {
      toast.error("Mic denied. Enable microphone access to use Voice Walk.");
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch {}
    }
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setPhase("transcribing");
  };

  const sendAudio = async () => {
    try {
      const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
      chunksRef.current = [];
      try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
      streamRef.current = null;
      if (blob.size < 500) {
        toast.error("That was too short — try again");
        setPhase("idle"); setSeconds(0); return;
      }
      const fd = new FormData();
      fd.append("file", blob, "voice-walk.webm");
      const { data } = await api.post(`/jobs/${jobId}/voice-walk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setTranscript(data?.transcript || "");
      const list = (data?.appliances || []).map((a, i) => ({
        id: `${Date.now()}-${i}`,
        ...a,
      }));
      setRows(list);
      if (list.length === 0) {
        toast.warning("No appliances detected. Try again or add manually.");
      } else {
        toast.success(`Found ${list.length} appliance${list.length === 1 ? "" : "s"}`);
      }
      setPhase("review");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcribe failed");
      setPhase("idle"); setSeconds(0);
    }
  };

  const update = (id, patch) => setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const remove = (id) => setRows(prev => prev.filter(r => r.id !== id));
  const addBlank = () => setRows(prev => [...prev, {
    id: `${Date.now()}-blank`, type: "", brand: "", model: "", serial: "", location: "Kitchen", notes: "",
  }]);

  const saveAll = async () => {
    const ready = rows.filter(r => r.type || r.brand || r.model || r.serial);
    if (ready.length === 0) { toast.error("Nothing to save"); return; }
    setPhase("saving");
    let ok = 0, fail = 0;
    const queue = [...ready];
    await Promise.all(Array.from({ length: Math.min(5, queue.length) }, async () => {
      while (queue.length) {
        const r = queue.shift();
        try {
          await api.post(`/jobs/${jobId}/appliance`, {
            type: r.type || "", brand: r.brand || "", model: r.model || "",
            serial: r.serial || "", location: r.location || "Kitchen",
            notes: r.notes || "", save_to_property: true,
          });
          ok++;
        } catch { fail++; }
      }
    }));
    if (fail === 0) toast.success(`Saved ${ok} appliance${ok === 1 ? "" : "s"}`);
    else toast.warning(`Saved ${ok}, ${fail} failed`);
    await onSaved?.();
    onClose?.();
  };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-[95] bg-black/70 flex items-start justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white border-2 border-[#0f172a] shadow-[10px_10px_0px_#0f172a] my-6"
        onClick={e => e.stopPropagation()} data-testid="voice-walk-modal">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a] bg-gradient-to-r from-red-600 via-fuchsia-600 to-violet-600">
          <div className="text-white">
            <div className="font-display font-black uppercase text-lg tracking-tight inline-flex items-center gap-2">
              <Waveform weight="fill" size={20}/> Voice Walk
            </div>
            <div className="text-[11px] font-medium opacity-90">
              Narrate the walkthrough · we transcribe + extract every appliance · you review & save
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="voice-walk-close"
            className="h-9 w-9 border-2 border-white text-white font-black hover:bg-white/10"><X weight="bold" size={16}/></button>
        </div>

        {phase === "idle" && (
          <div className="p-6 text-center" data-testid="voice-walk-idle">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-red-500 to-fuchsia-600 border-4 border-[#0f172a] rounded-full flex items-center justify-center shadow-[6px_6px_0px_#0f172a]">
              <Microphone weight="fill" size={44} className="text-white"/>
            </div>
            <div className="mt-4 font-display font-black text-xl uppercase">Tap to Start</div>
            <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
              Speak naturally: <em>"Fridge Whirlpool serial ABC123, washer LG in laundry, water heater Rheem 2019 downstairs, smoke detectors upstairs Kidde."</em> We stop automatically after 4.5 s of silence.
            </p>
            <button onClick={start} data-testid="voice-walk-start"
              className="tactile-btn mt-5 h-14 px-8 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest inline-flex items-center gap-2">
              <Microphone weight="fill" size={20}/> Start Recording
            </button>
          </div>
        )}

        {phase === "recording" && (
          <div className="p-6 text-center" data-testid="voice-walk-recording">
            <div className="mx-auto w-32 h-32 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, rgba(220,38,38,${0.35 + level * 0.5}) 0%, rgba(220,38,38,0.15) 60%, transparent 80%)`,
                transform: `scale(${1 + level * 0.15})`,
                transition: "transform 80ms linear",
              }}>
              <div className="w-24 h-24 bg-red-600 border-4 border-white rounded-full flex items-center justify-center animate-pulse">
                <Microphone weight="fill" size={40} className="text-white"/>
              </div>
            </div>
            <div className="mt-4 font-display font-black text-3xl tabular-nums">{mm}:{ss}</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">
              Silence auto-stops · Max {Math.floor(MAX_SECONDS / 60)} min
            </div>
            <button onClick={stop} data-testid="voice-walk-stop"
              className="tactile-btn mt-5 h-12 px-6 bg-[#0f172a] hover:bg-black text-white font-black uppercase tracking-widest inline-flex items-center gap-2">
              <Stop weight="fill" size={16}/> Stop & Extract
            </button>
          </div>
        )}

        {phase === "transcribing" && (
          <div className="p-10 text-center" data-testid="voice-walk-transcribing">
            <Sparkle weight="fill" size={48} className="mx-auto text-fuchsia-500 animate-pulse"/>
            <div className="mt-3 font-display font-black text-lg uppercase">Transcribing & Extracting</div>
            <p className="mt-1 text-sm text-slate-500">Whisper is listening · Claude is parsing every appliance…</p>
          </div>
        )}

        {phase === "review" && (
          <div className="p-4" data-testid="voice-walk-review">
            {transcript && (
              <details className="mb-3 border-2 border-slate-200 bg-slate-50 p-2">
                <summary className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">
                  Transcript ({transcript.length} chars)
                </summary>
                <div className="mt-2 text-xs text-slate-700 italic whitespace-pre-wrap" data-testid="voice-walk-transcript">
                  "{transcript}"
                </div>
              </details>
            )}
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                {rows.length} appliance{rows.length === 1 ? "" : "s"} detected
              </div>
              <div className="flex gap-2">
                <button onClick={addBlank} data-testid="voice-walk-add-blank"
                  className="h-8 px-3 border-2 border-[#0f172a] text-[10px] font-black uppercase tracking-widest hover:bg-slate-50">
                  + Add manually
                </button>
                <button onClick={() => { setRows([]); setTranscript(""); setPhase("idle"); setSeconds(0); }}
                  data-testid="voice-walk-redo"
                  className="h-8 px-3 border-2 border-red-500 text-red-600 text-[10px] font-black uppercase tracking-widest hover:bg-red-50">
                  Redo
                </button>
              </div>
            </div>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="voice-walk-list">
              {rows.length === 0 && (
                <div className="p-6 text-center text-sm italic text-slate-500 border-2 border-dashed border-slate-300">
                  No appliances detected. Add manually or redo the recording.
                </div>
              )}
              {rows.map(r => <ApplianceReviewRow key={r.id} row={r} onChange={update} onRemove={remove}/>)}
            </div>
          </div>
        )}

        <div className="p-4 border-t-2 border-[#0f172a] bg-slate-50 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
            <Sparkle weight="fill" size={11} className="text-fuchsia-500"/> Whisper + Claude Sonnet 4.5
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-11 px-4 border-2 border-slate-300 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-white">
              Cancel
            </button>
            {phase === "review" && (
              <button onClick={saveAll} disabled={rows.length === 0}
                data-testid="voice-walk-save-all"
                className="tactile-btn h-11 px-5 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1">
                <CheckCircle weight="fill" size={14}/> Save All ({rows.length})
              </button>
            )}
            {phase === "saving" && (
              <div className="h-11 px-5 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 opacity-70">
                <Sparkle weight="fill" size={14} className="animate-pulse"/> Saving…
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplianceReviewRow({ row, onChange, onRemove }) {
  const set = (k) => (e) => onChange(row.id, { [k]: e.target.value });
  return (
    <div className="p-2 bg-white border-2 border-[#0f172a] grid grid-cols-2 md:grid-cols-6 gap-2 items-end"
      data-testid={`voice-walk-row-${row.id}`}>
      <label className="col-span-2 md:col-span-1">
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Type</div>
        <select value={row.type} onChange={set("type")} data-testid={`vw-type-${row.id}`}
          className="w-full h-9 px-2 border-2 border-slate-300 text-xs font-medium bg-white">
          <option value="">— Pick —</option>
          {APPLIANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          {row.type && !APPLIANCE_TYPES.includes(row.type) && <option value={row.type}>{row.type} (AI)</option>}
        </select>
      </label>
      <label>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Location</div>
        <select value={row.location} onChange={set("location")} data-testid={`vw-loc-${row.id}`}
          className="w-full h-9 px-2 border-2 border-slate-300 text-xs font-medium bg-white">
          {LOCATION_PRESETS.map(l => <option key={l} value={l}>{l}</option>)}
          {row.location && !LOCATION_PRESETS.includes(row.location) && <option value={row.location}>{row.location}</option>}
        </select>
      </label>
      <label>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Brand</div>
        <input value={row.brand} onChange={set("brand")} placeholder="Whirlpool" data-testid={`vw-brand-${row.id}`}
          className="w-full h-9 px-2 border-2 border-slate-300 text-xs"/>
      </label>
      <label>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Model</div>
        <input value={row.model} onChange={set("model")} placeholder="Model #" data-testid={`vw-model-${row.id}`}
          className="w-full h-9 px-2 border-2 border-slate-300 text-xs"/>
      </label>
      <label>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Serial</div>
        <input value={row.serial} onChange={set("serial")} placeholder="Serial #" data-testid={`vw-serial-${row.id}`}
          className="w-full h-9 px-2 border-2 border-slate-300 text-xs"/>
      </label>
      <button onClick={() => onRemove(row.id)} data-testid={`vw-remove-${row.id}`}
        className="h-9 border-2 border-red-500 text-red-500 hover:bg-red-50 flex items-center justify-center">
        <Trash weight="bold" size={13}/>
      </button>
    </div>
  );
}
